import { Link, useLocation } from "wouter";
import { ChevronDown, Globe, X, Menu as MenuIcon } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useAdmin } from "@/hooks/use-admin";
import { ReactNode, useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { NotificationBell } from "@/components/notifications/NotificationBell";

interface NavHeaderProps {
  breadcrumb?: ReactNode;
  actions?: ReactNode;
}

export function NavHeader({ breadcrumb, actions }: NavHeaderProps = {}) {
  const { user, isLoading, logoutMutation } = useAuth();
  const { isAdmin } = useAdmin();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [, setLocation] = useLocation();

  const navigateTo = (path: string) => {
    setMobileMenuOpen(false);
    setLocation(path);
  };

  return (
    <>
      <header className="border-b border-white/[0.06] bg-clubhouse-black/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link href="/">
              <span className="text-white/90 text-[14px] font-medium tracking-widest uppercase inline-flex items-center gap-2 leading-none">
                <Globe className="h-4 w-4 shrink-0" />
                <span>Clubhouse FM</span>
              </span>
            </Link>
            {breadcrumb && (
              <div className="flex items-center text-white/30">
                {breadcrumb}
              </div>
            )}
          </div>

          <nav className="flex items-center gap-1">
            {!isLoading && user && (
              <>
                <Link href="/booking/new" className="hidden sm:block">
                  <button className="px-4 py-1.5 rounded-full bg-white text-black text-[14px] font-medium hover:bg-white/90 active:bg-white/80 active:scale-[0.97] transition-all">
                    New booking
                  </button>
                </Link>

                <NotificationBell />

                {actions}

                {/* Mobile menu button */}
                <button
                  onClick={() => setMobileMenuOpen(true)}
                  className="sm:hidden flex items-center gap-1 px-3 py-1.5 rounded-lg text-[14px] text-white/50 hover:text-white/80 hover:bg-white/[0.04] transition-colors"
                >
                  Menu
                  <ChevronDown className="h-3.5 w-3.5 opacity-50" />
                </button>

                {/* Desktop dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="hidden sm:flex items-center gap-1 px-3 py-1.5 rounded-lg text-[14px] text-white/50 hover:text-white/80 hover:bg-white/[0.04] transition-colors">
                      Menu
                      <ChevronDown className="h-3.5 w-3.5 opacity-50" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-[#1a1a1a] border-white/[0.08] min-w-[180px]">
                    <DropdownMenuItem asChild className="text-white/70 hover:text-white focus:text-white focus:bg-white/[0.06] text-[14px]">
                      <Link href="/my-bookings">My Bookings</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild className="text-white/70 hover:text-white focus:text-white focus:bg-white/[0.06] text-[14px]">
                      <Link href="/profile/notifications">Notification Settings</Link>
                    </DropdownMenuItem>

                    {isAdmin && (
                      <>
                        <DropdownMenuSeparator className="bg-white/[0.06]" />
                        <DropdownMenuItem asChild className="text-white/70 hover:text-white focus:text-white focus:bg-white/[0.06] text-[14px]">
                          <Link href="/admin/bookings">Admin Bookings</Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild className="text-white/70 hover:text-white focus:text-white focus:bg-white/[0.06] text-[14px]">
                          <Link href="/admin/users">Admin Users</Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild className="text-white/70 hover:text-white focus:text-white focus:bg-white/[0.06] text-[14px]">
                          <Link href="/admin/gofundme">GoFundMe Links</Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild className="text-white/70 hover:text-white focus:text-white focus:bg-white/[0.06] text-[14px]">
                          <Link href="/admin/calendar">Manage Calendar</Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild className="text-white/70 hover:text-white focus:text-white focus:bg-white/[0.06] text-[14px]">
                          <Link href="/admin/private-recordings">Private Recordings</Link>
                        </DropdownMenuItem>
                      </>
                    )}

                    <DropdownMenuSeparator className="bg-white/[0.06]" />
                    <DropdownMenuItem
                      onClick={() => logoutMutation.mutate()}
                      className="text-red-400/80 hover:text-red-400 focus:text-red-400 focus:bg-white/[0.06] text-[14px]"
                    >
                      Log out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
          </nav>
        </div>
      </header>

      {/* Mobile bottom sheet menu */}
      {mobileMenuOpen && (
        <div className="sm:hidden fixed inset-0 z-[100]">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/80 animate-in fade-in duration-200"
            onClick={() => setMobileMenuOpen(false)}
          />
          {/* Sheet */}
          <div className="absolute bottom-0 left-0 right-0 bg-[#171717] rounded-t-2xl border-t border-white/[0.08] p-6 pb-10 animate-in slide-in-from-bottom duration-300">
            {/* Handle */}
            <div className="flex justify-center mb-6">
              <div className="w-10 h-1 rounded-full bg-white/20" />
            </div>

            <nav className="space-y-1">
              <button
                onClick={() => navigateTo("/booking/new")}
                className="w-full text-left px-4 py-3 rounded-xl text-[16px] text-white font-medium hover:bg-white/[0.06] transition-colors"
              >
                New Booking
              </button>
              <button
                onClick={() => navigateTo("/my-bookings")}
                className="w-full text-left px-4 py-3 rounded-xl text-[16px] text-white/70 hover:bg-white/[0.06] transition-colors"
              >
                My Bookings
              </button>
              <button
                onClick={() => navigateTo("/profile/notifications")}
                className="w-full text-left px-4 py-3 rounded-xl text-[16px] text-white/70 hover:bg-white/[0.06] transition-colors"
              >
                Notification Settings
              </button>

              {isAdmin && (
                <>
                  <div className="border-t border-white/[0.06] my-2" />
                  <button
                    onClick={() => navigateTo("/admin/bookings")}
                    className="w-full text-left px-4 py-3 rounded-xl text-[16px] text-white/70 hover:bg-white/[0.06] transition-colors"
                  >
                    Admin Bookings
                  </button>
                  <button
                    onClick={() => navigateTo("/admin/users")}
                    className="w-full text-left px-4 py-3 rounded-xl text-[16px] text-white/70 hover:bg-white/[0.06] transition-colors"
                  >
                    Admin Users
                  </button>
                  <button
                    onClick={() => navigateTo("/admin/gofundme")}
                    className="w-full text-left px-4 py-3 rounded-xl text-[16px] text-white/70 hover:bg-white/[0.06] transition-colors"
                  >
                    GoFundMe Links
                  </button>
                  <button
                    onClick={() => navigateTo("/admin/calendar")}
                    className="w-full text-left px-4 py-3 rounded-xl text-[16px] text-white/70 hover:bg-white/[0.06] transition-colors"
                  >
                    Manage Calendar
                  </button>
                  <button
                    onClick={() => navigateTo("/admin/private-recordings")}
                    className="w-full text-left px-4 py-3 rounded-xl text-[16px] text-white/70 hover:bg-white/[0.06] transition-colors"
                  >
                    Private Recordings
                  </button>
                </>
              )}

              <div className="border-t border-white/[0.06] my-2" />
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  logoutMutation.mutate();
                }}
                className="w-full text-left px-4 py-3 rounded-xl text-[16px] text-red-400/80 hover:bg-white/[0.06] transition-colors"
              >
                Log out
              </button>
            </nav>
          </div>
        </div>
      )}
    </>
  );
}
